'use client';

import React, { useState, useEffect } from 'react';
import { useDashboard } from '@/components/dashboard/DashboardContext';
import { canViewChannel } from '@/utils/discordPermissions';
import ChannelItem from './ChannelItem';
import { Channel } from '@/types';
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent
} from '@/components/ui/collapsible';
import { Separator } from '@/components/ui/separator';
import { ChevronDown, ChevronRight, Loader2, FolderClosed } from 'lucide-react';

// 채널 그룹핑을 위한 인터페이스
interface CategoryGroup {
  category: Channel;
  channels: Channel[];
}

const ChannelList = () => {
  const { channels, isLoadingChannels, currentChannelId, currentGuildId, selectChannel } = useDashboard();
  const [openCategories, setOpenCategories] = useState<{ [key: string]: boolean }>({});

  // 카테고리 목록 초기화
  useEffect(() => {
    if (channels && channels.length > 0) {
      const categoryIds = channels
        .filter(channel => channel.type === 'category')
        .map(category => category.id);

      // 모든 카테고리를 기본적으로 열린 상태로 설정
      const initialOpenState: { [key: string]: boolean } = {};
      categoryIds.forEach(id => {
        initialOpenState[id] = true;
      });
      setOpenCategories(initialOpenState);
    }
  }, [channels]);

  // 카테고리 토글 핸들러
  const handleToggleCategory = (categoryId: string) => {
    setOpenCategories(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  // 채널 선택 핸들러
  const handleSelectChannel = (channelId: string) => {
    if (currentGuildId) {
      selectChannel(currentGuildId, channelId);
    }
  };

  if (isLoadingChannels) {
    return (
      <div className="flex justify-center items-center p-4">
        <Loader2 className="h-4 w-4 animate-spin text-primary dark:text-primary" />
      </div>
    );
  }

  // 채널이 없는 경우
  if (!channels || channels.length === 0) {
    return (
      <div className="px-4 py-6 text-center">
        <p className="text-sm text-muted-foreground dark:text-muted-foreground">채널이 없습니다</p>
      </div>
    );
  }

  // 채널 및 카테고리 준비
  const organizeChannels = () => {

    // 볼 수 있는 채널 선별 - 사용자가 ViewChannel 권한이 있는 채널만 포함
    const visibleChannels = channels.filter(channel => {
      // 카테고리는 항상 표시 (하위 채널이 있는지는 나중에 확인)
      if (channel.type === 'category') {
        return true;
      }

      // 일반 채널은 ViewChannel 권한이 있는 경우만 표시
      return canViewChannel(channel.permissions);
    });

    // 채널을 위치 순으로 정렬
    const sortedChannels = [...visibleChannels].sort((a, b) => a.position - b.position);

    // 카테고리와 일반 채널 분리
    const categoryChannels = sortedChannels.filter(channel => channel.type === 'category');
    const regularChannels = sortedChannels.filter(channel => channel.type !== 'category');

    // 카테고리가 없는 채널들 (상단에 표시될 채널들)
    const uncategorizedChannels = regularChannels.filter(channel => !channel.parent_id);

    // 카테고리별 채널 그룹
    const categoryGroups: CategoryGroup[] = [];
    categoryChannels.forEach(category => {
      const categoryChildren = regularChannels.filter(
        channel => channel.parent_id === category.id
      );

      // 카테고리에 보이는 채널이 하나라도 있는 경우에만 카테고리 그룹에 추가
      if (categoryChildren.length > 0) {
        categoryGroups.push({
          category,
          channels: categoryChildren
        });
      }
    });

    return { uncategorizedChannels, categoryGroups };
  };

  const { uncategorizedChannels, categoryGroups } = organizeChannels();

  return (
    <div className="px-0.5">
      {/* 카테고리가 없는 채널들을 먼저 상단에 표시 */}
      {uncategorizedChannels.map((channel) => (
        <ChannelItem
          key={channel.id}
          channel={channel}
          isSelected={channel.id === currentChannelId}
          onClick={() => handleSelectChannel(channel.id)}
        />
      ))}

      {/* 카테고리 그룹들 표시 */}
      {categoryGroups.map((group, index) => (
        <Collapsible
          key={group.category.id}
          open={openCategories[group.category.id] || false}
          onOpenChange={(open) => setOpenCategories({ ...openCategories, [group.category.id]: open })}
          className={`${index > 0 ? 'mt-1' : ''}`}
        >
          <CollapsibleTrigger
            className="flex items-center w-full px-0 py-1.5 rounded-md hover:!bg-[var(--hover-item)] hover:!text-[var(--selected-item-text)]"
            onClick={() => handleToggleCategory(group.category.id)}
          >
            <span className="text-foreground dark:text-foreground">{group.category.name}</span>

            <div className="flex items-center text-xs font-medium text-muted-foreground dark:text-muted-foreground uppercase tracking-wider px-1.5">
              {openCategories[group.category.id] ? (
                <ChevronDown className="h-3.5 w-3.5 mr-1 text-foreground dark:text-foreground" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 mr-1 text-foreground dark:text-foreground" />
              )}

            </div>
          </CollapsibleTrigger>

          <CollapsibleContent>
            <div>
              {group.channels.map((channel) => (
                <ChannelItem
                  key={channel.id}
                  channel={channel}
                  isSelected={channel.id === currentChannelId}
                  onClick={() => handleSelectChannel(channel.id)}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      ))}
    </div>
  );
};

export default ChannelList;
